import React from 'react';
import {
  Pressable, Text, Spinner, Box, VStack, Icon, Card, useToken,
} from '@gluestack-ui/themed';
import { Image } from 'expo-image';
import { FileText, Loader2 } from 'lucide-react-native';
import Animated, {
  useAnimatedStyle, withRepeat, withTiming, useSharedValue, withSequence,
} from 'react-native-reanimated';

const LoadingProgressBar = () => {
  const offset = useSharedValue(-100);
  const brandBase = useToken('colors', 'brandBase');

  React.useEffect(() => {
    offset.value = withRepeat(
      withSequence(
        withTiming(100, { duration: 1500 }),
        withTiming(-100, { duration: 0 }),
      ),
      -1,
    );
  }, [offset]);

  const style = useAnimatedStyle(() => ({
    transform: [{ translateX: offset.value }],
  }));

  return (
    <Animated.View
      style={[
        {
          width: '100%',
          height: '100%',
          backgroundColor: brandBase,
        },
        style,
      ]}
    />
  );
};

const AttachmentItem = ({ attachment, isDeleting, triggerProps }) => {
  const isUploading = attachment._isOptimistic; // eslint-disable-line no-underscore-dangle

  const rotation = useSharedValue(0);

  React.useEffect(() => {
    if (isUploading) {
      rotation.value = withRepeat(withTiming(360, { duration: 1000 }), -1);
    } else {
      rotation.value = 0;
    }
  }, [isUploading, rotation]);

  const spinStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  if (!attachment.filename) return null;

  const extension = attachment.filename.split('.').pop()?.toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(extension);

  return (
    <Pressable {...triggerProps} isDisabled={isUploading}>
      <Card w={60} h={60} bg="$backgroundLight100" borderRadius="$md" overflow="hidden" p={0} m="$1" borderWidth={isUploading ? 1 : 0} borderColor="$brandSubtle">
        <VStack flex={1} alignItems="center" justifyContent="center">
          {/* Thumbnail Area */}
          <Box h={60} w={60} borderRadius="$md" alignItems="center" justifyContent="center" overflow="hidden" flex={1}>
            {isDeleting ? (
              <Box flex={1} alignItems="center" justifyContent="center">
                <Spinner size="small" />
              </Box>
            ) : isUploading ? (
              <VStack space="xs" alignItems="center" justifyContent="center" px="$1" w="100%">
                <Animated.View style={spinStyle}>
                  <Icon as={Loader2} size="md" color="$brandBase" />
                </Animated.View>
                <Box w="80%" h={2} bg="$backgroundLight200" borderRadius="$full" overflow="hidden">
                  <LoadingProgressBar />
                </Box>
              </VStack>
            ) : isImage ? (
              <Image
                source={{ uri: attachment.thumbnailUrl }}
                alt="Attachment Thumbnail"
                style={{
                  width: '100%',
                  height: '100%',
                }}
                contentFit="cover"
                cachePolicy="memory-disk"
                transition={200}
              />
            ) : (
              <Icon as={FileText} size="xl" color="$textLight400" />
            )}
          </Box>

          {/* Filename Area - Hide during upload for better focus on progress */}
          {!isUploading && (
            <VStack space="xs" flex={1} w="100%" maxHeight={30} position="absolute" bottom={0} left={0} right={0}>
              <Text
                fontSize="$2xs"
                color="$white"
                numberOfLines={2}
                textAlign="center"
                bg="$backgroundLight600"
                opacity={0.8}
                p="$1"
              >
                {attachment.filename.split('.')[0]}
              </Text>
            </VStack>
          )}
        </VStack>
      </Card>
    </Pressable>
  );
};

export default AttachmentItem;
